import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import CustomSelect from "./common/CustomSelect";
import { Icon } from "@iconify/react/dist/iconify.js";
import ErrorAlert from "./ui/ErrorAlert";

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("1");
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [modalError, setModalError] = useState("");
  const { hasPermission } = usePermissions();

  const navigate = useNavigate();

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      setError(null);
      const response = await apiCall(
        "get",
        "/get-all-products",
        null,
        navigate
      );

      if (response?.data?.success && response?.data?.data) {
        setProducts(response.data.data);
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "GET");
      } else {
        handleCustomResponse("error", "Failed to load products.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      setError(true);
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchPlans = async (productId = null) => {
    const productIdToUse = productId || selectedProduct;

    if (!productIdToUse || productIdToUse === "" || productIdToUse === "pev") {
      handleCustomResponse("error", "Please select a valid product first.");
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      setError(null);

      const response = await apiCall(
        "get",
        "/get-pricing-plans-pev",
        { product_id: productIdToUse },
        navigate
      );

      if (response?.data?.success && response?.data?.data) {
        setPlans(response.data.data);
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "GET");
      } else {
        setPlans([]);
        handleCustomResponse(
          "error",
          "No plans found for the selected product."
        );
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      setError(true);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = (selected) => {
    const productId = selected ? selected.value : "";
    setSelectedProduct(productId);
    if (productId && productId !== "") {
      fetchPlans(productId);
    } else {
      setPlans([]);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchPlans("1");
  }, []);

  const handleOpenDeleteModal = (plan) => {
    if (!plan?.plan_id) {
      handleCustomResponse("error", "Plan ID is missing for this plan.");
      return;
    }
    setSelectedPlan(plan);
    setShowModal(true);
    setModalError("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPlan(null);
    setModalError("");
  };

  const handleDeletePlan = async () => {
    if (!selectedPlan?.plan_id) {
      handleCustomResponse("error", "Plan ID is missing.");
      setModalError("Plan ID is missing. Please close and try again.");
      return;
    }

    setLoading(true);

    try {
      const response = await apiCall(
        "delete",
        `/delete-pricing-plan/${selectedPlan.plan_id}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "DELETE");
        await fetchPlans();
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to delete plan.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "DELETE");
    } finally {
      setLoading(false);
    }
  };

  if ((loading && plans.length === 0) || productsLoading) {
    return <LoadingComponent />;
  }
  if (error) {
    return <ErrorAlert />;
  }

  return (
    <>
      <style>
        {`
          .plans-page .plans-container {
            padding: var(--size-32) var(--size-16);
            width: 100%;
            min-height: 100vh;
            margin: 0 auto;
            box-sizing: border-box;
          }

          .plans-page .product-selector-section {
            border-radius: var(--rounded-16);
            padding: var(--size-24);
            margin-bottom: var(--size-32);
            box-shadow: var(--shadow-4);
            border: 2px solid var(--primary-200);
          }

          .plans-page .selector-header {
            display: flex;
            align-items: center;
            gap: var(--size-12);
            margin-bottom: var(--size-16);
          }

          .plans-page .selector-header .info-icon {
            font-size: var(--font-2xl);
            color: var(--primary-600);
          }

          .plans-page .selector-header h3 {
            font-size: var(--font-xl);
            font-weight: 600;
            color: var(--neutral-800);
            margin: 0;
          }

          .plans-page .selector-content {
            display: flex;
            flex-direction: column;
            gap: var(--size-16);
          }

          .plans-page .selector-row {
            display: flex;
            align-items: center;
            gap: var(--size-16);
            flex-wrap: wrap;
          }

          .plans-page .selector-row label {
            font-size: var(--font-md);
            font-weight: 500;
            color: var(--neutral-700);
            min-width: 120px;
          }

          .plans-page .selector-row .select-wrapper {
            flex: 1;
            min-width: 250px;
          }

          .plans-page .plans-header {
            text-align: center;
            margin-bottom: var(--size-32);
          }

          .plans-page .plans-header h2 {
            font-size: var(--font-2xl);
            font-weight: 600;
            color: var(--neutral-800);
            margin-bottom: var(--size-8);
          }

          .plans-page .plans-header p {
            font-size: var(--font-md);
            color: var(--text-secondary-light);
          }

          .plans-page .plans-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: var(--size-24);
            align-items: stretch;
            width: 100%;
          }

          .plans-page .plan-card {
            background: var(--white);
            border-radius: var(--rounded-16);
            box-shadow: var(--shadow-4);
            padding: var(--size-24);
            text-align: left;
            transition: all 0.3s ease;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            height: 100%;
            min-height: 450px;
            width: 100%;
            box-sizing: border-box;
            border: 2px solid transparent;
          }

          /* Add to your existing CSS */
.plans-page .modal-button.danger {
  background: var(--error-600, #dc3545); /* Fallback color */
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.plans-page .modal-button.danger:disabled {
  background: var(--error-600, #dc3545);
  color: white;
  opacity: 0.6;
  cursor: not-allowed;
}

.plans-page .spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

:root {
  --error-600: #dc3545;
  --error-700: #c82333;
}

          .plans-page .plan-card:hover {
            background-color: var(--primary-400);
            color: var(--white);
            transform: translateY(-4px);
            box-shadow: var(--shadow-8);
          }

          .plans-page .plan-card .plan-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: var(--size-16);
            flex-wrap: wrap;
            gap: var(--size-8);
          }

          .plans-page .plan-card h3 {
            font-size: var(--font-xxl);
            font-weight: 600;
            color: var(--neutral-800);
            margin: 0;
            transition: color 0.3s;
          }

          .plans-page .plan-card:hover h3 {
            color: var(--white);
          }

          .plans-page .plan-card .price {
            font-size: var(--font-2xl);
            font-weight: bold;
            color: var(--primary-700);
            margin-bottom: var(--size-8);
            transition: color 0.3s;
          }

          .plans-page .plan-card:hover .price {
            color: var(--white);
          }

          .plans-page .plan-card .segment {
            font-size: var(--font-sm);
            color: var(--text-secondary-light);
            margin-bottom: var(--size-16);
            text-transform: capitalize;
            transition: color 0.3s;
          }

          .plans-page .plan-card:hover .segment {
            color: var(--white);
          }

          .plans-page .plan-card .description {
            font-size: var(--font-sm);
            color: var(--text-secondary-light);
            margin-bottom: var(--size-16);
            transition: color 0.3s;
          }

          .plans-page .plan-card:hover .description {
            color: var(--white);
          }

          .plans-page .plan-card ul {
            list-style: none;
            padding: 0;
            margin-bottom: var(--size-24);
            flex-grow: 1;
          }

          .plans-page .plan-card ul li {
            font-size: var(--font-sm);
            color: var(--text-secondary-light);
            margin-bottom: var(--size-8);
            display: flex;
            align-items: flex-start;
            gap: var(--size-8);
            transition: color 0.3s;
          }

          .plans-page .plan-card:hover ul li {
            color: var(--white);
          }

          .plans-page .plan-card ul li .icon {
            color: var(--primary-700);
            font-size: var(--font-md);
            flex-shrink: 0;
            margin-top: 2px;
            transition: color 0.3s;
          }

          .plans-page .plan-card:hover ul li .icon {
            color: var(--light-600);
          }

          .plans-page .plan-card .action-buttons {
            display: flex;
            gap: var(--size-8);
            margin-top: auto;
          }

          .plans-page .plan-card .action-button {
            flex: 1;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: var(--size-8);
            padding: var(--size-12) var(--size-16);
            border: 2px solid var(--primary-700);
            background: transparent;
            color: var(--primary-700);
            border-radius: var(--rounded-8);
            font-size: var(--font-sm);
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s;
            text-decoration: none;
          }

          .plans-page .plan-card .action-button:hover {
            background: var(--primary-700);
            color: var(--white);
          }

          .plans-page .plan-card:hover .action-button {
            border-color: var(--white);
            color: var(--white);
          }

          .plans-page .plan-card:hover .action-button:hover {
            background: var(--white);
            color: var(--primary-700);
          }

          .plans-page .plan-card .action-button.delete {
            border-color: var(--error-600);
            color: var(--error-600);
          }

          .plans-page .plan-card .action-button.delete:hover {
            background: var(--error-600);
            color: var(--white);
          }

          .plans-page .plan-card:hover .action-button.delete {
            border-color: var(--white);
            color: var(--white);
          }

          .plans-page .plan-card:hover .action-button.delete:hover {
            background: var(--white);
            color: var(--error-600);
          }

          .plans-page .add-plan-button {
            display: inline-flex;
            align-items: center;
            gap: var(--size-8);
            padding: var(--size-12) var(--size-24);
            background: var(--primary-700);
            color: white;
            border: none;
            border-radius: var(--rounded-8);
            font-size: var(--font-md);
            font-weight: 500;
            cursor: pointer;
            transition: background 0.3s;
            text-decoration: none;
            margin-bottom: var(--size-24);
          }

          .plans-page .add-plan-button:hover {
            background: var(--primary-900);
          }

          .plans-page .no-plans-container {
            text-align: center;
            padding: var(--size-32);
            font-size: var(--font-md);
            color: var(--text-secondary-light);
            background: var(--white);
            border-radius: var(--rounded-12);
            border: 2px dashed var(--neutral-300);
          }

          .plans-page .alert-info {
            text-align: center;
            padding: var(--size-16);
            background: var(--info-50);
            color: var(--info-700);
            border-radius: var(--rounded-8);
            border: 1px solid var(--info-200);
          }

          /* Modal Styles */
          .plans-page .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
          }

          .plans-page .modal-content {
            background: var(--white);
            border-radius: var(--rounded-16);
            padding: var(--size-24);
            max-width: 500px;
            width: 90%;
            box-shadow: var(--shadow-8);
          }

          .plans-page .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: var(--size-16);
          }

          .plans-page .modal-header h5 {
            font-size: var(--font-xl);
            font-weight: 600;
            color: var(--neutral-800);
            margin: 0;
          }

          .plans-page .modal-close {
            background: transparent;
            border: none;
            font-size: var(--font-xl);
            cursor: pointer;
            color: var(--neutral-600);
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .plans-page .modal-body {
            margin-bottom: var(--size-24);
          }

          .plans-page .modal-footer {
            display: flex;
            gap: var(--size-12);
          }

          .plans-page .modal-button {
            flex: 1;
            padding: var(--size-12) var(--size-24);
            border: none;
            border-radius: var(--rounded-8);
            font-size: var(--font-md);
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s;
          }

          .plans-page .modal-button.secondary {
            background: var(--neutral-200);
            color: var(--neutral-700);
          }

          .plans-page .modal-button.secondary:hover {
            background: var(--neutral-300);
          }

          .plans-page .modal-button.danger {
            background: var(--error-600);
            color: white;
          }

          .plans-page .modal-button.danger:hover {
            background: var(--error-700);
          }

          @media (max-width: 768px) {
            .plans-page .plans-container {
              padding: var(--size-16) var(--size-12);
            }

            .plans-page .plans-grid {
              grid-template-columns: 1fr;
              gap: var(--size-16);
            }

            .plans-page .selector-row {
              flex-direction: column;
              align-items: stretch;
            }

            .plans-page .selector-row label {
              min-width: auto;
            }

            .plans-page .plan-card .action-buttons {
              flex-direction: column;
            }
          }

          @media (min-width: 1400px) {
            .plans-page .plans-container {
              padding: var(--size-32) var(--size-32);
            }

            .plans-page .plans-grid {
              grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
              gap: var(--size-32);
            }
          }
        `}
      </style>

      <div className="plans-page">
        <div className="plans-container">
          <div className="plans-header">
            <h2>Manage Subscription Plans</h2>
            <p>
              Create, update, or remove subscription plans. These plans will be
              available for clients to choose from in their portal.
            </p>
          </div>

          {/* Product Selector Section */}
          <div className="product-selector-section">
            <div className="selector-header">
              <Icon icon="mdi:package-variant" className="info-icon" />
              <h3>Product Selection</h3>
            </div>
            <div className="selector-content">
              <div className="selector-row">
                <label htmlFor="productSelect">Select Product:</label>
                <div className="select-wrapper">
                  <CustomSelect
                    name="productSelect"
                    value={selectedProduct}
                    onChange={handleProductChange}
                    options={products}
                    placeholder="Choose a product..."
                    labelKey={
                      products[0]?.name
                        ? "name"
                        : products[0]?.product_name
                        ? "product_name"
                        : "product_id"
                    }
                    valueKey="product_id"
                    isLoading={productsLoading}
                    isClearable={true}
                    isSearchable={true}
                    isMulti={false}
                  />
                </div>
              </div>
              {hasPermission("insert_plan") && selectedProduct && (
                <div>
                  <Link
                    to={`/add-plan?product_id=${selectedProduct}`}
                    className="add-plan-button"
                  >
                    <Icon icon="mdi:plus" />
                    Add New Plan
                  </Link>
                </div>
              )}
            </div>
          </div>

          {!selectedProduct ? (
            <div className="alert-info">
              Please select a product to view its pricing plans.
            </div>
          ) : (
            <div className="plans-grid">
              {!loading && plans.length === 0 ? (
                <div className="no-plans-container">
                  <Icon
                    icon="mdi:alert-circle-outline"
                    style={{
                      fontSize: "48px",
                      marginBottom: "16px",
                      color: "var(--neutral-400)",
                    }}
                  />
                  <p>No plans found for the selected product.</p>
                </div>
              ) : (
                plans.map((plan) => (
                  <div className="plan-card" key={plan.plan_id}>
                    <div>
                      <div className="plan-header">
                        <h3>{plan.plan_name}</h3>
                      </div>
                      <div className="price">
                        {plan.base_price != null
                          ? `$${plan.base_price}`
                          : "N/A"}
                        <span
                          style={{
                            fontSize: "var(--font-md)",
                            fontWeight: "normal",
                          }}
                        >
                          /month
                        </span>
                      </div>
                      <div className="segment">
                        For {plan.customer_segment || "General"} Businesses
                      </div>
                      <div className="description">
                        {plan.description || "No description available."}
                      </div>
                      <ul>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Per Vehicle Fee: ${plan.per_vehicle_fee ?? "N/A"}
                        </li>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Volume Threshold: {plan.volume_threshold ?? "N/A"}
                        </li>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Min Vehicles: {plan.min_vehicle_count ?? "N/A"}
                        </li>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Max Vehicles: {plan.max_vehicle_count ?? "N/A"}
                        </li>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Max Allowed: {plan.max_vehicles_allowed ?? "N/A"}
                        </li>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Trial Plan: {plan.is_trial_plan ? "Yes" : "No"}
                        </li>
                      </ul>
                    </div>

                    <div className="action-buttons">
                      {hasPermission("update_plan") && (
                        <Link
                          to={`/update-plan/${plan?.plan_id}`}
                          state={{ plan }}
                          className="action-button"
                        >
                          <Icon icon="mdi:pencil" />
                          Edit
                        </Link>
                      )}
                      {hasPermission("delete_plan") && (
                        <button
                          className="action-button delete"
                          onClick={() => handleOpenDeleteModal(plan)}
                        >
                          <Icon icon="mdi:delete" />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Delete Modal */}
        {/* Delete Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Delete Plan</h5>
                <button
                  className="modal-close"
                  onClick={handleCloseModal}
                  disabled={loading}
                >
                  <Icon icon="mdi:close" />
                </button>
              </div>
              <div className="modal-body">
                {modalError && (
                  <div
                    style={{
                      padding: "var(--size-12)",
                      background: "var(--error-50)",
                      color: "var(--error-700)",
                      borderRadius: "var(--rounded-8)",
                      marginBottom: "var(--size-16)",
                    }}
                  >
                    {modalError}
                  </div>
                )}
                <p style={{ margin: 0, color: "var(--neutral-700)" }}>
                  Are you sure you want to delete the plan:{" "}
                  <strong>{selectedPlan?.plan_name || "this plan"}</strong>?
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="modal-button secondary"
                  onClick={handleCloseModal}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="modal-button danger"
                  onClick={handleDeletePlan}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Icon
                        icon="mdi:loading"
                        className="spin"
                        style={{ marginRight: "8px" }}
                      />
                      Deleting...
                    </>
                  ) : (
                    "Delete Plan"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && plans.length > 0 && !showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9998,
            }}
          >
            <LoadingComponent />
          </div>
        )}

        {loading && plans.length > 0 && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </div>
    </>
  );
};

export default Plans;
